import { debounce, lowerCase, startCase } from 'lodash-es'
import { DateTime, Interval } from 'luxon'
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { LuHistory, LuX } from 'react-icons/lu'

import { toaster } from '@/components/Toaster'
import { findTaskSchedule, TaskStatus, useTasksBoardAll } from '@/feature/execution-board'
import { FeatureFlag, useFlag, WhenFlag } from '@/feature/feature-flags'
import { reverseGeocode } from '@/feature/map'
import { ActiveAssetContext } from '@/feature/observatory/contexts/ActiveAssetContext'
import { useSettings } from '@/feature/organizations/api/useSettings'
import { ReplayContext } from '@/feature/replay'
import { TagsModalContainer, TagsPopout } from '@/feature/tags'
import { useAssetTelemetry } from '@/feature/telemetry'
import { captureError } from '@/utils/captureError'

import { CircleAvatar } from '@/lib/core/components/CircleAvatar'
import { CopyButton } from '@/lib/core/components/CopyButton'
import { Loader } from '@/lib/core/components/Loader'
import { Tab } from '@/lib/core/components/Tab'
import { TabPanel } from '@/lib/core/components/TabPanel'
import { Tabs } from '@/lib/core/components/Tabs'
import { Type, TypeBadgeFilled } from '@/lib/core/components/TypeBadgeFilled'
import { formatTimestamp } from '@/lib/core/constants/constants'

import { AssetContentType } from '@/lib/features/Assets/interfaces'
import { useAssetActiveShares } from '@/lib/features/SmartShares/api'

import styles from './AssetPanel.module.css'
import { AssetTagsTab } from './AssetTagsTab'
import { AssetTaskTab } from './AssetTaskTab'
import { ShareCard } from './ShareCard'

const AssetPanelTab = ({ value, children }: { value: string; children: ReactNode }) => (
  <Tab value={value}>
    {({ activeTab, setActiveTab }) => (
      <button
        type="button"
        className={[styles.tab, activeTab === value ? styles.active : ''].join(' ')}
        onClick={() => setActiveTab(value)}
      >
        {children}
      </button>
    )}
  </Tab>
)

export const AssetPanel = () => {
  const { activeAsset, clearActiveAsset } = useContext(ActiveAssetContext)
  const { setReplayAsset, available: replayAvailable } = useContext(ReplayContext)

  const [currentLocation, setCurrentLocation] = useState<string>()
  const [isLoadingAddress, setIsLoadingAddress] = useState(false)

  const tracking = useAssetTelemetry(activeAsset?.vehicleBridgeId)
  const reverseGeocodeMode = useFlag('reverseGeocodeMode', 'Google' as string)
  const { data: settings } = useSettings()
  const { data: sharesData } = useAssetActiveShares(
    activeAsset ? { assetId: activeAsset.id, pageSize: 100 } : null,
  )
  const isTasksEnabled = useFlag('featureExecutionBoardUi', false)
  const isTagsEnabled = useFlag('assetTags', false)
  const { data: tasksData, isLoading: isTasksLoading } = useTasksBoardAll(
    isTasksEnabled && activeAsset?.objectId
      ? {
          assignedAsset: activeAsset.objectId,
          status: [TaskStatus.IN_PROGRESS, TaskStatus.SCHEDULED],
        }
      : null,
  )

  const tasks = tasksData?.results ?? []

  const inShare = useMemo(() => {
    if (!activeAsset || !sharesData?.results) return undefined
    return sharesData.results.find(
      (share) => share.bridgeStatus === 'borrowed' && share.assetId === activeAsset.id,
    )
  }, [activeAsset, sharesData?.results])

  const outShares = useMemo(() => {
    if (!activeAsset || !sharesData?.results) return []
    return sharesData.results.filter(
      (share) =>
        share.bridgeStatus === 'lent' &&
        share.assetId === activeAsset.id &&
        Interval.fromDateTimes(
          DateTime.fromISO(share.dateFrom),
          DateTime.fromISO(share.dateTo),
        ).contains(DateTime.local()),
    )
  }, [activeAsset, sharesData?.results])

  const hasLostContact = useMemo(
    () =>
      tracking?.sensorDatetime != undefined &&
      DateTime.fromJSDate(tracking.sensorDatetime) <
        DateTime.now().minus({ minutes: settings?.vehicleOfflineAfter ?? 24 * 60 }),
    [tracking?.sensorDatetime, settings?.vehicleOfflineAfter],
  )

  const statusText = hasLostContact ? 'Offline' : tracking?.isMoving ? 'Moving' : 'Still'

  const getGeocodeData = useCallback(
    debounce(async (lat: number, lng: number) => {
      try {
        setIsLoadingAddress(true)
        const address = await reverseGeocode(lat, lng, reverseGeocodeMode)
        if (address) {
          setCurrentLocation(address)
        }
      } catch (error) {
        toaster({ message: 'Unable to determine location address', type: 'error' })
        captureError()(error as Error)
      } finally {
        setIsLoadingAddress(false)
      }
    }, 500),
    [reverseGeocodeMode],
  )

  useEffect(() => {
    if (activeAsset && tracking) {
      getGeocodeData(tracking.latitude, tracking.longitude)
    } else {
      setCurrentLocation(undefined)
    }
  }, [activeAsset, tracking?.latitude, tracking?.longitude])

  const getCopyText = useCallback(() => {
    if (!activeAsset) return ''

    const lines: string[] = []

    // Asset Overview data
    lines.push(`Asset ID: ${activeAsset.id}`)
    lines.push(
      `${activeAsset.contentType === AssetContentType.PTU ? 'Tracking ID' : 'Registration'}: ${activeAsset.contentType === AssetContentType.PTU ? activeAsset.trackingIdentifier : activeAsset.plateNumber}`,
    )
    lines.push(`Status: ${statusText}`)
    lines.push(
      `Last Seen: ${tracking?.sensorDatetime ? formatTimestamp(tracking.sensorDatetime) : '?'}`,
    )
    lines.push(`Latitude: ${tracking?.latitude == null ? '?' : tracking.latitude}`)
    lines.push(`Longitude: ${tracking?.longitude == null ? '?' : tracking.longitude}`)
    if (activeAsset.contentType !== AssetContentType.PTU) {
      lines.push(`Speed: ${tracking?.speed == null ? '?' : `${Math.round(tracking.speed)} km/h`}`)
    }
    lines.push(`Current Location: ${currentLocation ?? '-'}`)

    // Share Details data
    if (inShare) {
      lines.push('')
      lines.push(`Received from: ${inShare.organization?.name || '-'}`)
      if (inShare.contactFrom) {
        lines.push(`Contact: ${inShare.contactFrom.firstName} ${inShare.contactFrom.lastName}`)
        if (inShare.contactFrom.phoneNumber) lines.push(`Phone: ${inShare.contactFrom.phoneNumber}`)
        if (inShare.contactFrom.email) lines.push(`Email: ${inShare.contactFrom.email}`)
      }
    }
    outShares.forEach((share) => {
      lines.push('')
      lines.push(`Shared with: ${share.organization?.name || '-'}`)
      if (share.contactTo) {
        lines.push(`Contact: ${share.contactTo.firstName} ${share.contactTo.lastName}`)
        if (share.contactTo.phoneNumber) lines.push(`Phone: ${share.contactTo.phoneNumber}`)
        if (share.contactTo.email) lines.push(`Email: ${share.contactTo.email}`)
      }
    })
    if (!inShare && outShares.length === 0) {
      lines.push('')
      lines.push('Shares: No active shares')
    }

    // Tasks data
    tasks.forEach((task) => {
      const taskDef = task.taskDefinition
      const startLocation = taskDef.locations?.[0]
      const endLocation = taskDef.locations?.[taskDef.locations.length - 1]
      const schedule = findTaskSchedule(task)
      lines.push('')
      lines.push(`Task Reference: ${taskDef.reference || '-'}`)
      lines.push(`Issued By: ${task.assignedByOrg.name}`)
      lines.push(`Assigned To: ${task.onwardAssignedToOrg?.name || task.assignedByOrg.name}`)
      lines.push(`Description: ${taskDef.description || '-'}`)
      lines.push(`From: ${startLocation?.name || startLocation?.address || '-'}`)
      lines.push(`From Date: ${schedule ? formatTimestamp(schedule.start) : '-'}`)
      lines.push(`To: ${endLocation?.name || endLocation?.address || '-'}`)
      lines.push(`To Date: ${schedule ? formatTimestamp(schedule.end) : '-'}`)
      lines.push(`Status: ${startCase(lowerCase(task.status))}`)
    })

    return lines.join('\n')
  }, [activeAsset, tracking, statusText, currentLocation, inShare, outShares, tasks])

  if (!activeAsset) return null

  const isOpen = !!activeAsset

  return (
    <Tabs defaultValue="overview">
      <div className={[styles.drawer, isOpen ? styles.open : ''].join(' ')}>
        <div className={styles.header}>
          <div className={styles.tabs}>
            <AssetPanelTab value="overview">Asset Overview</AssetPanelTab>
            <AssetPanelTab value="share">Share Details</AssetPanelTab>
            <FeatureFlag flag="featureExecutionBoardUi">
              <WhenFlag>
                <AssetPanelTab value="tasks">Tasks</AssetPanelTab>
              </WhenFlag>
            </FeatureFlag>
            {isTagsEnabled && <AssetPanelTab value="tags">Tags</AssetPanelTab>}
          </div>
          <div className={styles.actions}>
            <CopyButton getText={getCopyText} className={styles.actionButton} size={18} />
            {replayAvailable && (
              <FeatureFlag flag="featureReplay">
                <WhenFlag>
                  <button
                    type="button"
                    className={styles.actionButton}
                    onClick={() => setReplayAsset(activeAsset)}
                  >
                    <LuHistory />
                    <span>Replay</span>
                  </button>
                </WhenFlag>
              </FeatureFlag>
            )}
            <button type="button" className={styles.closeButton} onClick={clearActiveAsset}>
              <LuX size={20} />
            </button>
          </div>
        </div>

        <TabPanel value="overview">
          {({ isActive }) =>
            isActive ? (
              <div className={styles.content}>
                {!tracking ? (
                  <div className={styles.column}>
                    <span className={styles.detailValue}>No tracking data available</span>
                  </div>
                ) : (
                  <div className={styles.column}>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>ID:</span>
                      <span className={styles.detailValue}>{activeAsset.id}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>
                        {activeAsset.contentType === AssetContentType.PTU
                          ? 'Tracking ID:'
                          : 'Registration:'}
                      </span>
                      <span className={styles.detailValue}>
                        {activeAsset.contentType === AssetContentType.PTU
                          ? activeAsset.trackingIdentifier
                          : activeAsset.plateNumber}
                      </span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Status:</span>
                      <span className={styles.detailValue}>{statusText}</span>
                    </div>
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Last Seen:</span>
                      <span className={styles.detailValue}>
                        {tracking?.sensorDatetime ? formatTimestamp(tracking.sensorDatetime) : '?'}
                      </span>
                    </div>
                  </div>
                )}

                <div className={styles.column}>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Latitude:</span>
                    <span className={styles.detailValue}>
                      {tracking?.latitude == null ? '?' : `${tracking.latitude}`}
                    </span>
                  </div>
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Longitude:</span>
                    <span className={styles.detailValue}>
                      {tracking?.longitude == null ? '?' : `${tracking.longitude}`}
                    </span>
                  </div>
                  {/* Stickers never report speed and are ingested as PTUs for now, so speed is
                      hidden for PTUs. TODO: switch to AssetContentType.STICKER once it exists. */}
                  {activeAsset.contentType !== AssetContentType.PTU && (
                    <div className={styles.detailRow}>
                      <span className={styles.detailLabel}>Speed:</span>
                      <span className={styles.detailValue}>
                        {tracking?.speed == null ? 'N/A' : `${Math.round(tracking.speed)} km/h`}
                      </span>
                    </div>
                  )}
                  <div className={styles.detailRow}>
                    <span className={styles.detailLabel}>Current Location:</span>
                    <span className={styles.detailValue}>
                      {isLoadingAddress ? <Loader /> : (currentLocation ?? '-')}
                    </span>
                  </div>
                </div>

                <div className={styles.column}>
                  <div className={styles.columnTitle}>Shares</div>
                  {outShares.map((share) => (
                    <ShareCard
                      key={share.id}
                      type="out"
                      organizationName={share.organization?.name}
                      reference={share.reference}
                    />
                  ))}
                  {inShare && (
                    <ShareCard
                      type="in"
                      organizationName={inShare.organization?.name}
                      reference={inShare.reference}
                    />
                  )}
                </div>
              </div>
            ) : null
          }
        </TabPanel>

        <TabPanel value="share">
          {({ isActive }) =>
            isActive ? (
              <div className={styles.content}>
                {inShare && (
                  <div className={styles.sharesColumn}>
                    <div className={styles.shareHeader}>
                      <span className={styles.sharesDetailValue}>Received from:</span>
                      <TypeBadgeFilled type={Type.IN} />
                    </div>
                    <div className={styles.organizationRow}>
                      <CircleAvatar
                        src={inShare.organization?.avatar}
                        size={24}
                        alt={inShare.organization?.name || 'Organization'}
                      />
                      <span className={styles.detailValue}>
                        {inShare.organization?.name || '-'}
                      </span>
                    </div>
                    <span className={styles.sharesDetailValue}>Share Ref:</span>
                    <span className={styles.detailValue}>{inShare.reference || '-'}</span>
                    <span className={styles.sharesDetailValue}>Contact:</span>
                    <div className={styles.contactDetails}>
                      <span>
                        {inShare.contactFrom
                          ? `${inShare.contactFrom.firstName} ${inShare.contactFrom.lastName}`
                          : 'No details provided'}
                      </span>
                      {inShare.contactFrom?.phoneNumber && (
                        <span>{inShare.contactFrom.phoneNumber}</span>
                      )}
                      {inShare.contactFrom?.email && <span>{inShare.contactFrom.email}</span>}
                    </div>
                  </div>
                )}
                {outShares.map((share) => (
                  <div key={share.id} className={styles.sharesColumn}>
                    <div className={styles.shareHeader}>
                      <span className={styles.sharesDetailValue}>Shared with:</span>
                      <TypeBadgeFilled type={Type.OUT} />
                    </div>
                    <div className={styles.organizationRow}>
                      <CircleAvatar
                        src={share.organization?.avatar}
                        size={24}
                        alt={share.organization?.name || 'Organization'}
                      />
                      <span className={styles.detailValue}>{share.organization?.name || '-'}</span>
                    </div>
                    <span className={styles.sharesDetailValue}>Share Ref:</span>
                    <span className={styles.detailValue}>{share.reference || '-'}</span>
                    <span className={styles.sharesDetailValue}>Contact:</span>
                    <div className={styles.contactDetails}>
                      <span>
                        {share.contactTo
                          ? `${share.contactTo.firstName} ${share.contactTo.lastName}`
                          : 'No details provided'}
                      </span>
                      {share.contactTo?.phoneNumber && <span>{share.contactTo.phoneNumber}</span>}
                      {share.contactTo?.email && <span>{share.contactTo.email}</span>}
                    </div>
                  </div>
                ))}
                {!inShare && outShares.length === 0 && (
                  <span className={styles.detailValue}>No active shares</span>
                )}
              </div>
            ) : null
          }
        </TabPanel>
        <FeatureFlag flag="featureExecutionBoardUi">
          <WhenFlag>
            <TabPanel value="tasks">
              {({ isActive }) =>
                isActive ? <AssetTaskTab tasks={tasks} isLoading={isTasksLoading} /> : null
              }
            </TabPanel>
          </WhenFlag>
        </FeatureFlag>
        {isTagsEnabled && (
          <TabPanel value="tags">
            {({ isActive }) => (isActive ? <AssetTagsTab asset={activeAsset} /> : null)}
          </TabPanel>
        )}
      </div>
      {/* Mounted outside the drawer on purpose: the drawer is transformed, which would make it the
          containing block for these `position: fixed` popouts and offset them from the viewport
          coordinates they are positioned with. */}
      {isTagsEnabled && (
        <>
          <TagsPopout />
          <TagsModalContainer />
        </>
      )}
    </Tabs>
  )
}

export default AssetPanel
